import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import {
  DEFAULT_JURISDICTIONS_CONFIG,
  classifyCountry,
  type JurisdictionsConfig,
} from "./compliance/constants";

export { classifyCountry } from "./compliance/constants";

export const RESTRICTED_JURISDICTIONS = ["US", "CA", "BE", "NZ"] as const;
export type RestrictedJurisdiction = (typeof RESTRICTED_JURISDICTIONS)[number];

export function isRestricted(code: string | null | undefined): boolean {
  if (!code) return false;
  return (RESTRICTED_JURISDICTIONS as readonly string[]).includes(code.toUpperCase());
}

/**
 * Best-effort country detection from the Cloudflare edge header.
 * Also returns the live restricted/warning/ok classification.
 */
export const detectCountry = createServerFn({ method: "GET" }).handler(async () => {
  let code: string | null = null;
  try {
    const req = getRequest();
    const headerVal =
      req.headers.get("cf-ipcountry") ??
      req.headers.get("CF-IPCountry") ??
      req.headers.get("x-vercel-ip-country");
    const up = (headerVal ?? "").toUpperCase();
    if (/^[A-Z]{2}$/.test(up)) code = up;
  } catch {
    // ignore — runs outside request context
  }

  let cfg: JurisdictionsConfig = DEFAULT_JURISDICTIONS_CONFIG;
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("site_settings")
      .select("value")
      .eq("key", "compliance_jurisdictions")
      .maybeSingle();
    if (data?.value) cfg = data.value as unknown as JurisdictionsConfig;
  } catch {
    // ignore
  }

  return {
    country: code,
    classification: classifyCountry(code, cfg),
    restricted: code ? cfg.blocked.includes(code) : false,
    warning: code ? cfg.warning.includes(code) : false,
  };
});
